import { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react';
import {
  usePrograms,
  useCohorts,
  useTalentList,
  useSubscription,
  useAuth as useSdkAuth,
} from '@tonnie/sdk';
import { useTranslation } from '@tonnie/locale';
import type { Program, Cohort, Talent } from '@/core/types/business';
import { toTalentView } from './talent-view';

interface BusinessContextType {
  selectedProgramId: string | null;
  setSelectedProgramId: (id: string | null) => void;
  selectedCohortId: string | null;
  setSelectedCohortId: (id: string | null) => void;
  programs: Program[];
  cohorts: Cohort[];
  talents: Talent[];
  /**
   * How many ACTIVE talents the server holds, and how many are archived.
   * (W7.1 · DW7-1)
   *
   * Server-supplied, and deliberately NOT `talents.length`: the array is one
   * page. Before this, the context discarded the endpoint's `total` entirely,
   * so every consumer that wanted a population count had only the page length
   * — which is how five hero pages came to render both tab badges from
   * `array.length` over capped arrays.
   */
  /** The server's tab counts — `null` until it has answered. */
  talentTotals: { active: number; archived: number } | null;
  /** `true` when the server holds active talents this page did not fetch. */
  talentsTruncated: boolean;
  refreshData: () => void;
  // Subscription plan state
  isEnterprise: boolean;
  // Loading states for SDK queries
  isLoading: boolean;
  /**
   * `true` when ANY of the underlying queries (programs / cohorts /
   * talents) has failed. Surfaces so hero pages can render a
   * `<SectionErrorState>` with a retry action instead of silently
   * dropping to the empty grid. Mirrors `isLoading` — boolean OR
   * across the three queries.
   */
  isError: boolean;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

/**
 * BusinessContextProvider
 *
 * Renders ONE stable element type in every auth state, so the routed subtree
 * below it (Router → business hero pages) mounts ONCE and survives the
 * signed-out → signed-in → token-ready transitions.
 *
 * Previously this swapped between `<BusinessContext.Provider>` (a placeholder
 * empty context while a token was being minted) and `<AuthenticatedBusinessProvider>`
 * (once ready), gated on an async `getToken()`. When that gate flipped LATE —
 * under load, AFTER the user had already interacted with a page — React could
 * not reconcile the two different element types and REMOUNTED the whole subtree,
 * silently resetting page-local UI state (observed: the Care page's active tab
 * jumped back from "Talents" to the default while an admin was mid-task; block-15).
 *
 * The empty-context placeholder existed only to keep the SDK business hooks from
 * firing before a token was on the client (401s on login/signup). Those hooks
 * (usePrograms / useCohorts / useTalentList / useSubscription) now self-gate on
 * `isSignedIn && !!token`, so they no longer fire prematurely — the subtree swap
 * is unnecessary, and removing it eliminates the remount at the source for EVERY
 * business hero page (Care / Vetting / Mobility / Adaptation / Evidence).
 */
export function BusinessContextProvider({ children }: { children: ReactNode }) {
  return <AuthenticatedBusinessProvider>{children}</AuthenticatedBusinessProvider>;
}

function AuthenticatedBusinessProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { isLoaded, isSignedIn, token } = useSdkAuth();
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const [selectedCohortId, setSelectedCohortId] = useState<string | null>(null);

  // SDK hooks — self-gate on `isSignedIn && !!token`, so they stay idle (no
  // 401s) until the token is on the client even though this provider now mounts
  // in every auth state.
  const {
    data: programsData,
    isLoading: programsLoading,
    isError: programsError,
    refetch: refetchPrograms,
  } = usePrograms();
  const {
    data: cohortsData,
    isLoading: cohortsLoading,
    isError: cohortsError,
    refetch: refetchCohorts,
  } = useCohorts();
  /**
   * The portal-wide talent list. (W7.1 · DW7-1)
   *
   * This call used to be `useTalents()` — no arguments at all, so it took the
   * controller's default of 20 rows AND paid one S3 presign per row for
   * avatars nothing in this context renders. It is the root of the 20-row cap:
   * Home, Vetting, Mobility, Adaptation, Care, Evidence and Pool all read
   * `talents` from here, so every count, percentage and tab badge derived from
   * it was computed over a page of 20 regardless of the real population.
   *
   * `view: 'active'` is what every consumer of this array wanted (the archived
   * lists are fetched separately, per page). `limit` is the board ceiling, the
   * same one the Pipeline asks for — sized for the import wizard's 500-row
   * batch. `photos: false` is the whole reason that is affordable.
   *
   * `totals` is exposed alongside, so a page that needs "how many are there"
   * stops reaching for `talents.length`.
   */
  const talentList = useTalentList({
    view: 'active',
    limit: 500,
    photos: false,
  });
  const talentsLoading = talentList.isLoading;
  const talentsError = talentList.isError;
  const refetchTalents = talentList.refetch;
  const { data: subscription } = useSubscription();

  // Map SDK data to local types (SDK returns may differ slightly in shape)
  const programs = useMemo<Program[]>(
    () => (programsData as unknown as Program[]) ?? [],
    [programsData]
  );
  const cohorts = useMemo<Cohort[]>(
    () => (cohortsData as unknown as Cohort[]) ?? [],
    [cohortsData]
  );
  const talents = useMemo<Talent[]>(
    () => talentList.rows.map((row) => toTalentView(row, t('common.unknown'))),
    [talentList.rows, t]
  );

  // Treat the pre-token window (signed in, token not yet on the client) as
  // loading too. The business queries self-gate on the token, so before it
  // lands they're DISABLED and report `isLoading: false` — if we surfaced that,
  // a consumer would render its interactive content early, then the token would
  // arrive, the queries would enable, and `isLoading` would flip false→true→false,
  // UNMOUNTING and remounting that content mid-interaction (a page whose body is
  // gated behind `if (isLoading)` loses its local UI state — this is exactly how
  // the Care page's active tab reset from "Talents" to default; block-15).
  // Holding `isLoading` true continuously until the token lands AND the first
  // fetch resolves makes the consumer mount its content exactly once. (v5
  // `isLoading` is first-load only, so later refetches keep it false.)
  const awaitingToken = isSignedIn && !token;
  const isLoading =
    !isLoaded || awaitingToken || programsLoading || cohortsLoading || talentsLoading;
  // Aggregate error state across the three core SDK queries. Hero
  // pages consume this to render `<SectionErrorState>` with retry.
  const isError = programsError || cohortsError || talentsError;

  // The SERVER's Enterprise entitlement (`BillingEntitlementsService.isEnterprise`,
  // the same decision every Enterprise-only API route enforces), never
  // re-derived from the plan label. False until the subscription loads.
  const isEnterprise = subscription?.isEnterprise ?? false;

  const refreshData = useCallback(() => {
    refetchPrograms();
    refetchCohorts();
    refetchTalents();
  }, [refetchPrograms, refetchCohorts, refetchTalents]);

  return (
    <BusinessContext.Provider
      value={{
        selectedProgramId,
        setSelectedProgramId,
        selectedCohortId,
        setSelectedCohortId,
        programs,
        cohorts,
        talents,
        talentTotals: talentList.totals,
        talentsTruncated: talentList.hasMore,
        refreshData,
        isEnterprise,
        isLoading,
        isError,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusinessContext() {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusinessContext must be used within a BusinessContextProvider');
  }
  return context;
}
